import { CapType, FilterType as FilterOperator } from "types/enums";

import {
    FilterAffiliateRule,
    FilterCapRule,
    FilterColumn,
    FilterContext,
    FilterFieldRule,
    FilterGroup,
    FilterRule,
    FilterTimeRule,
    FilterWeekdayRule,
} from "../types/filterEditor";
import { FilterLogicalOperator } from "../types/filterTree";

let idCounter = 0;

export const createFilterId = (): string => {
    idCounter += 1;
    return `filter-node-${idCounter}`;
};

export const createGroup = (operator: FilterLogicalOperator = "and", rules: FilterRule[] = []): FilterGroup => ({
    id: createFilterId(),
    kind: "group",
    operator,
    rules,
});

export const createAffiliateRule = (
    mode: FilterAffiliateRule["mode"] = "in",
    affiliateIds: string[] = []
): FilterAffiliateRule => ({
    id: createFilterId(),
    kind: "affiliate",
    mode,
    affiliateIds,
});

export const createFieldRule = (overrides: Partial<Omit<FilterFieldRule, "id" | "kind">> = {}): FilterFieldRule => ({
    id: createFilterId(),
    kind: "field",
    tag: "",
    operator: FilterOperator.Equals,
    value: "",
    valueMax: "",
    ...overrides,
});

export const createCapRule = (overrides: Partial<Omit<FilterCapRule, "id" | "kind">> = {}): FilterCapRule => ({
    id: createFilterId(),
    kind: "cap",
    tag: CapType.Day,
    operator: FilterOperator.IsLessThan,
    value: "",
    ...overrides,
});

export const createTimeRule = (overrides: Partial<Omit<FilterTimeRule, "id" | "kind">> = {}): FilterTimeRule => ({
    id: createFilterId(),
    kind: "time",
    operator: FilterOperator.IsBetween,
    value: "",
    valueMax: "",
    ...overrides,
});

export const createWeekdayRule = (mode: FilterWeekdayRule["mode"] = "in", days: number[] = []): FilterWeekdayRule => ({
    id: createFilterId(),
    kind: "weekday",
    mode,
    days,
});

/** Cap tags are counted per channel in a cascade and per offer (clicks/leads) in an offer. */
export const getCapTypesForContext = (context: FilterContext): CapType[] => {
    const cascadeCaps = [
        CapType.All,
        CapType.Year,
        CapType.Month,
        CapType.Week,
        CapType.Day,
        CapType.Hour,
        CapType.Minute,
    ];
    if (context === "cascade") {
        return cascadeCaps;
    }
    return Object.values(CapType).filter((cap) => !cascadeCaps.includes(cap));
};

/** Same order as the original "column" select. A new row starts with the first column. */
export const FILTER_COLUMNS: FilterColumn[] = ["affiliate", "leads_data", "lead_count_limit", "time", "weekday"];

export const DEFAULT_FILTER_COLUMN: FilterColumn = FILTER_COLUMNS[0];

export const getRuleColumn = (rule: FilterRule): FilterColumn => {
    switch (rule.kind) {
        case "affiliate":
            return "affiliate";
        case "cap":
            return "lead_count_limit";
        case "time":
            return "time";
        case "weekday":
            return "weekday";
        default:
            return "leads_data";
    }
};

export const createRuleForColumn = (column: FilterColumn, context: FilterContext): FilterRule => {
    switch (column) {
        case "affiliate":
            return createAffiliateRule("in");
        case "lead_count_limit":
            return createCapRule({ tag: getCapTypesForContext(context).find((cap) => cap.endsWith("_day")) });
        case "time":
            return createTimeRule();
        case "weekday":
            return createWeekdayRule();
        default:
            return createFieldRule();
    }
};
